import {
  CategoryTypes,
  forumsV1FeaturedThreadsList,
  OrganizationType,
  rdbV1TrendingListsList,
  rdbV2LatestList,
  rdbV2Nil100List,
  SiteType,
  v1CategoriesFeaturedHeadlinesDetail,
  v1CategoriesHeadlinesDetail2,
  v1OrganizationCompositeRankingsList,
  v1SitesHeadlinesForCategoryTypeDetail,
  v1SitesList,
  type V1SitesListParams,
  v1TeamRankingsRankingsList,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import {
  type INetworkPageProps,
  NetworkHome,
} from '@public/components/Home/NetworkHome';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { GraphSchema } from '@public/store/schemas/Graph';
import { OrganizationSchema } from '@public/store/schemas/Organization';
import type { AxiosError } from 'axios';
import type { GetServerSideProps } from 'next';

const title = 'On3.com | The Best of College and High School Sports';
const description = `On3 is the best resource for college and high school sports, recruiting, and NIL. Find all of your favorite team's information or browse our advanced player database.`;

interface INetworkHomePage extends INetworkPageProps, IGenericPageProps {}

const NetworkHomePage = (props: INetworkHomePage) => {
  const canonical = 'https://www.on3.com/';

  const schema = new GraphSchema({
    items: [new OrganizationSchema().toJSON()],
  });

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      schema={schema}
      title={title}
    >
      <NetworkHome {...props} />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  try {
    const year =
      siteData?.defaultYears?.footballSeasonYear || new Date().getFullYear();
    const hsYear =
      siteData?.defaultYears?.highSchoolYear || new Date().getFullYear();

    // Calling in 8 since we remove duplicates from carousel
    const collegeHeadlinesParams = {
      type: CategoryTypes.Sport,
      siteKey: 44,
      limit: 8,
    };
    const collegeBusinessHeadlinesParams = {
      category: 130,
      limit: 8,
    };
    const timeOut = {
      timeout: 2000,
    };

    const rankingsParams = {
      sportKey: 1,
      year,
      limit: 10,
    };

    const fanSiteParams: V1SitesListParams = {
      type: SiteType.Team,
    };

    const [
      featuredArticles,
      collegeSportsArticles,
      recruitingArticles,
      collegeBusinessArticles,
      nilHundred,
      latestCommits,
      hsArticles,
      highSchoolRankings,
      collegeRankings,
      trendingList,
      fanSites,
      megaboardData,
    ] = await Promise.allSettled([
      v1CategoriesFeaturedHeadlinesDetail(proxyApi, 8, timeOut),
      v1SitesHeadlinesForCategoryTypeDetail(proxyApi, collegeHeadlinesParams),
      v1CategoriesFeaturedHeadlinesDetail(proxyApi, 571985, timeOut),
      v1CategoriesHeadlinesDetail2(proxyApi, collegeBusinessHeadlinesParams),
      rdbV2Nil100List(proxyApi, { limit: 5 }),
      rdbV2LatestList(proxyApi, { limit: 5 }),
      v1CategoriesFeaturedHeadlinesDetail(proxyApi, 556984, timeOut),
      v1OrganizationCompositeRankingsList(proxyApi, {
        orgType: OrganizationType.HighSchool,
        items: 10,
        sportKey: 1,
        year: hsYear,
      }),
      v1TeamRankingsRankingsList(proxyApi, {
        ...rankingsParams,
        orgType: OrganizationType.College,
      }),
      rdbV1TrendingListsList(proxyApi, {
        trendingListKey: 1,
        siteKey: 44,
        limit: 10,
      }),
      v1SitesList(proxyApi, fanSiteParams),
      forumsV1FeaturedThreadsList(proxyApi, { limit: 5 }),
    ]);

    return {
      props: {
        pageType: 'home',
        siteData: siteData || [],
        userData: userData || {},
        featuredArticles: getFulfilledValue(featuredArticles),
        collegeSportsArticles: getFulfilledValue(collegeSportsArticles),
        recruitingArticles: getFulfilledValue(recruitingArticles),
        collegeBusinessArticles: getFulfilledValue(collegeBusinessArticles),
        nilHundred: getFulfilledValue(nilHundred),
        latestCommits: getFulfilledValue(latestCommits),
        hsArticles: getFulfilledValue(hsArticles),
        highSchoolRankings: getFulfilledValue(highSchoolRankings),
        collegeRankings: getFulfilledValue(collegeRankings),
        trendingList: getFulfilledValue(trendingList),
        fanSites: getFulfilledValue(fanSites),
        megaboardData: getFulfilledValue(megaboardData),
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(NetworkHomePage);
